import React, { useEffect } from "react";
import "./LearningPath.css";

const LEARNING_STAGES = [
  {
    id: "stage-1",
    number: 1,
    title: "Trading Foundations",
    description:
      "Understand markets, orders, price movement, volume, liquidity, and volatility.",
    lessons: [
      { id: "understanding-market", title: "Understanding the Market" },
      { id: "how-stocks-move", title: "How Stocks Move" },
      { id: "market-orders", title: "Market Orders and Limit Orders" },
      { id: "bid-ask-spread", title: "Bid, Ask, and Spread" },
      { id: "volume-liquidity", title: "Volume and Liquidity" },
      { id: "relative-volume", title: "Relative Volume" },
      { id: "float-share-supply", title: "Float and Share Supply" },
      { id: "volatility-price-range", title: "Volatility and Price Range" },
      { id: "average-true-range", title: "Average True Range (ATR)" },
    ],
  },
  {
    id: "stage-2",
    number: 2,
    title: "Technical Analysis",
    description:
      "Learn how candles, chart levels, momentum, VWAP, and breakouts work together.",
    lessons: [
      { id: "candlestick-basics", title: "Candlestick Basics" },
      { id: "support-resistance", title: "Support and Resistance Basics" },
      { id: "breakouts-false-breakouts", title: "Breakouts and False Breakouts" },
      { id: "vwap-basics", title: "VWAP Basics" },
      { id: "macd-basics", title: "MACD: Momentum and Confirmation" },
    ],
  },
  {
    id: "stage-3",
    number: 3,
    title: "Risk Management",
    description:
      "Protect capital with structured risk, position sizing, stops, and profit management.",
    lessons: [
      { id: "risk-reward-ratio", title: "Risk-to-Reward Ratio" },
      { id: "position-sizing", title: "Position Sizing" },
      { id: "stop-loss", title: "How to Use a Stop Loss" },
      { id: "profit-targets", title: "Profit Targets" },
      { id: "trailing-stops", title: "Trailing Stops" },
    ],
  },
  {
    id: "stage-4",
    number: 4,
    title: "Trade Execution",
    description:
      "Prepare, confirm, read market depth, and manage advanced execution risk.",
    lessons: [
      { id: "trade-planning", title: "Trade Planning" },
      { id: "entry-confirmation", title: "Entry Confirmation" },
      { id: "level-2-basics", title: "Level 2 Basics" },
      { id: "short-selling", title: "Introduction to Short Selling" },
    ],
  },
  {
    id: "stage-5",
    number: 5,
    title: "Trader Mindset",
    description:
      "Build patience, emotional control, consistency, and rule-based decisions.",
    lessons: [
      {
        id: "trading-psychology-discipline",
        title: "Trading Psychology and Discipline",
      },
    ],
  },
];

const ACTIVE_LESSON_IDS = LEARNING_STAGES.flatMap((stage) =>
  stage.lessons.map((lesson) => lesson.id)
);

function LearningPath({
  onBack,
  onSelectLesson,
  completedLessons = 0,
  selectedStage = null,
}) {
  useEffect(() => {
    if (!selectedStage) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    const timer = window.setTimeout(() => {
      const stageElement =
        document.getElementById(selectedStage);

      if (stageElement) {
        stageElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);

    return () => window.clearTimeout(timer);
  }, [selectedStage]);

  const progressLabel =
    completedLessons === 1
      ? "1 lesson complete"
      : `${completedLessons} lessons complete`;

  return (
    <section className="learningPath">
      <button
        type="button"
        className="learningPathBackButton"
        onClick={onBack}
      >
        ← Back to Academy
      </button>

      <header className="learningPathHeader">
        <div>
          <span>
            ANINATRADE STRUCTURED EDUCATION
          </span>

          <h1>🧭 Your Learning Path</h1>

         <p>
            Open any of the 24 lessons currently available in the
            Academy. Future roadmap lessons are tracked separately.
          </p> 
        </div>

        <div className="learningPathProgressCard">
          <span>CURRENT PROGRESS</span>

          <strong>{progressLabel}</strong>

          <p>
            Your completed lessons remain saved
            automatically.
          </p>
        </div>
      </header>

      <div className="learningStageList">
        {LEARNING_STAGES.map((stage) => (
          <article
            id={stage.id}
            className={
              selectedStage === stage.id
                ? "learningStageCard learningStageCardSelected"
                : "learningStageCard"
            }
            key={stage.id}
          >
            <div className="learningStageNumber">
              {stage.number}
            </div>

            <div className="learningStageContent">
              <span>STAGE {stage.number}</span>

              <h2>{stage.title}</h2>

              <p>{stage.description}</p>

              <div className="learningStageLessons">
                {stage.lessons.map(
                  (lesson, index) => {
                    const isAvailable =
                      ACTIVE_LESSON_IDS.includes(
                        lesson.id
                      );

                    return (
                      <button
                        type="button"
                        className={
                          isAvailable
                            ? "learningPathLesson learningPathLessonAvailable"
                            : "learningPathLesson"
                        }
                        key={lesson.id}
                        disabled={!isAvailable}
                        onClick={() => {
                          if (isAvailable) {
                            onSelectLesson(lesson.id);
                          }
                        }}
                      >
                        <span>{index + 1}</span>

                        <strong>
                          {lesson.title}
                        </strong>

                        <em>
                          {isAvailable
                            ? "Open lesson →"
                            : "Coming soon"}
                        </em>
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default LearningPath;